
import React, { useState, useEffect } from 'react';
import { Table, Button, Space, Typography, Alert, message } from 'antd';
import { EyeOutlined, CloseOutlined, CheckOutlined, ReloadOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import './AdminSupportRequests.css';

const { Title } = Typography;

const AdminSupportRequests = () => {
  const [dataSource, setDataSource] = useState([]);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const fetchSupportRecords = async () => {
    setLoading(true);
    try {
      const response = await fetch('/backend/get_all_support_records.php', {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
      });

      const data = await response.json();
      console.log('Données reçues de get_all_support_records:', data);
      if (response.ok) {
        setDataSource(data);
        setError(null);
      } else {
        if (response.status === 403) {
          message.error('Session expirée. Veuillez vous reconnecter.');
          navigate('/superadminlogin');
        } else {
          setError(data.error || 'Erreur lors de la récupération des enregistrements');
        }
      }
    } catch (error) {
      console.error('Erreur réseau:', error);
      setError('Erreur réseau lors de la récupération.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSupportRecords();
  }, []);

  const columns = [
    { title: 'Name', dataIndex: 'name', key: 'name' },
    { title: 'Date', dataIndex: 'date', key: 'date' },
    
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status) => (
        <span
          className={
            status?.toLowerCase() === 'approved'
              ? 'status-approved'
              : status?.toLowerCase() === 'rejected'
              ? 'status-rejected'
              : 'status-pending'
          }
        >
          {status}
        </span>
      ),
    },
    
    { title: 'Category', dataIndex: 'category', key: 'category' },
    { title: 'Service Provider', dataIndex: 'serviceProvider', key: 'serviceProvider' },
    {
      title: 'Action',
      key: 'action',
      render: (_, record) => (
        <Space size="middle">
          <Button
            icon={<EyeOutlined />}
            type="link"
            size="small"
            onClick={() => handleView(record)}
          >
            View
          </Button>
          {record.status?.toLowerCase() !== 'rejected' && (
            <Button
              icon={<CloseOutlined />}
              type="link"
              danger
              size="small"
              onClick={() => handleReject(record)}
            >
              Reject
            </Button>
          )}
          {record.status?.toLowerCase() !== 'approved' && (
            <Button
              icon={<CheckOutlined />}
              type="link"
              size="small"
              onClick={() => handleApprove(record)}
            >
              Approve
            </Button>
          )}
        </Space>
      ),
    },
  ];

  const handleView = (record) => {
    console.log('View:', record);
  };

  const handleReject = async (record) => {
    if (!record.key) {
      setError('ID de l\'enregistrement manquant.');
      return;
    }
    console.log('Rejecting record:', { id: record.key, status: 'Rejected' });
    try {
      const response = await fetch('/backend/update_support_status.php', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({ id: record.key, status: 'Rejected' }),
      });

      const result = await response.json();
      console.log('Reject response:', result);
      if (response.ok) {
        setDataSource((prev) =>
          prev.map((item) =>
            item.key === record.key ? { ...item, status: 'Rejected' } : item
          )
        );
        setError(null);
      } else {
        if (response.status === 403) {
          message.error('Session expirée. Veuillez vous reconnecter.');
          navigate('/superadminlogin');
        } else {
          setError(result.error || 'Erreur lors du rejet de l\'enregistrement');
        }
      }
    } catch (error) {
      console.error('Reject error:', error);
      setError('Erreur réseau lors du rejet.');
    }
  };

  const handleApprove = async (record) => {
    if (!record.key) {
      setError('ID de l\'enregistrement manquant.');
      return;
    }
    console.log('Approving record:', { id: record.key, status: 'Approved' });
    try {
      const response = await fetch('/backend/update_support_status.php', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({ id: record.key, status: 'Approved' }),
      });

      const result = await response.json();
      console.log('Approve response:', result);
      if (response.ok) {
        setDataSource((prev) =>
          prev.map((item) =>
            item.key === record.key ? { ...item, status: 'Approved' } : item
          )
        );
        setError(null);
      } else {
        if (response.status === 403) {
          message.error('Session expirée. Veuillez vous reconnecter.');
          navigate('/superadminlogin');
        } else {
          setError(result.error || 'Erreur lors de l\'approbation de l\'enregistrement');
        }
      }
    } catch (error) {
      console.error('Approve error:', error);
      setError('Erreur réseau lors de l\'approbation.');
    }
  };

  const handleSave = () => {
    console.log('Save all changes');
  };

  const approvedRecords = dataSource.filter((record) => record.status?.toLowerCase() === 'approved');
  const rejectedRecords = dataSource.filter((record) => record.status?.toLowerCase() === 'rejected');
  const pendingRecords = dataSource.filter((record) => record.status?.toLowerCase() === 'pending');

  return (
    <div className="support-requests-container">
      <div className="header-section">
        <Title level={3} className="support-requests-title">
          Support Requests
        </Title>
        <Button
          icon={<ReloadOutlined />}
          type="primary"
          onClick={fetchSupportRecords}
          loading={loading}
        >
          Rafraîchir
        </Button>
      </div>
      {error && (
        <Alert
          message={error}
          type="error"
          showIcon
          className="auth-alert auth-error"
          style={{ marginBottom: 20 }}
        />
      )}
      <div className="status-section">
        <Title level={4} className="status-title">
          Pending Requests
        </Title>
        <Table
          dataSource={pendingRecords}
          columns={columns}
          pagination={{ pageSize: 5 }}
          className="support-requests-table"
          locale={{ emptyText: 'Aucune demande en attente' }}
          loading={loading}
        />
      </div>
      <div className="status-section">
        <Title level={4} className="status-title">
          Approved Requests
        </Title>
        <Table
          dataSource={approvedRecords}
          columns={columns}
          pagination={{ pageSize: 5 }}
          className="support-requests-table"
          locale={{ emptyText: 'Aucune demande approuvée' }}
          loading={loading}
        />
      </div>
      <div className="status-section">
        <Title level={4} className="status-title">
          Rejected Requests
        </Title>
        <Table
          dataSource={rejectedRecords}
          columns={columns}
          pagination={{ pageSize: 5 }}
          className="support-requests-table"
          locale={{ emptyText: 'Aucune demande rejetée' }}
          loading={loading}
        />
      </div>
      <div className="save-button-container">
        <Button
          type="primary"
          size="large"
          className="save-button"
          onClick={handleSave}
        >
          SAVE
        </Button>
      </div>
    </div>
  );
};

export default AdminSupportRequests;
